/**
 * Route Registry
 *
 * Manages dynamic route registration for plugins
 */

import { RouteRegistry, RouteExtension } from '../types';

/**
 * Route registry implementation
 */
class RouteRegistryImpl implements RouteRegistry {
  private routes = new Map<string, RouteExtension>();
  private pathIndex = new Map<string, string>(); // path -> routeId
  private listeners = new Set<() => void>();

  /**
   * Subscribe to route changes
   * @param listener - Callback function to call when routes change
   * @returns Unsubscribe function
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of route changes
   */
  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  /**
   * Register a route
   */
  register(route: RouteExtension): void {
    if (this.routes.has(route.id)) {
      console.warn(`Route "${route.id}" is already registered. Overwriting.`);
    }

    // Validate route
    this.validateRoute(route);

    this.routes.set(route.id, route);
    this.pathIndex.set(route.path, route.id);

    // Register child routes recursively
    if (route.children && route.children.length > 0) {
      for (const child of route.children) {
        // Set parent ID if not already set
        if (!child.parentId) {
          child.parentId = route.id;
        }
        this.register(child);
      }
    }

    console.log(`[RouteRegistry] Registered route: ${route.id} (${route.path}) - plugin: ${route.pluginId}`);

    // Notify listeners of route change
    this.notify();
  }

  /**
   * Validate route configuration
   */
  private validateRoute(route: RouteExtension): void {
    if (!route.id) {
      throw new Error('Route ID is required');
    }

    if (!route.pluginId) {
      throw new Error(`Route "${route.id}" must have a pluginId`);
    }

    if (!route.path) {
      throw new Error(`Route "${route.id}" must have a path`);
    }

    if (!route.component) {
      throw new Error(`Route "${route.id}" must have a component`);
    }

    // Validate path format
    if (!route.path.startsWith('/')) {
      throw new Error(`Route path "${route.path}" must start with /`);
    }
  }

  /**
   * Unregister a route by ID
   */
  unregister(routeId: string): void {
    const route = this.routes.get(routeId);

    if (!route) {
      console.warn(`[RouteRegistry] Route not found: ${routeId}`);
      return;
    }

    // Unregister child routes first
    const children = this.getChildRoutes(routeId);
    for (const child of children) {
      this.unregister(child.id);
    }

    // Remove from indexes
    this.routes.delete(routeId);
    this.pathIndex.delete(route.path);

    console.log(`[RouteRegistry] Unregistered route: ${routeId}`);

    // Notify listeners of route change
    this.notify();
  }

  /**
   * Get route by ID
   */
  getRoute(routeId: string): RouteExtension | undefined {
    return this.routes.get(routeId);
  }

  /**
   * Get route by path
   */
  getRouteByPath(path: string): RouteExtension | undefined {
    const routeId = this.pathIndex.get(path);
    return routeId ? this.routes.get(routeId) : undefined;
  }

  /**
   * Get all routes
   */
  getAllRoutes(): RouteExtension[] {
    return Array.from(this.routes.values());
  }

  /**
   * Get routes by plugin ID
   */
  getRoutesByPlugin(pluginId: string): RouteExtension[] {
    return Array.from(this.routes.values()).filter(
      (route) => route.pluginId === pluginId
    );
  }

  /**
   * Get root routes (no parent)
   */
  getRootRoutes(): RouteExtension[] {
    return Array.from(this.routes.values()).filter(
      (route) => !route.parentId
    );
  }

  /**
   * Get child routes for a parent route
   */
  getChildRoutes(parentId: string): RouteExtension[] {
    return Array.from(this.routes.values()).filter(
      (route) => route.parentId === parentId
    );
  }

  /**
   * Clear all routes
   */
  clear(): void {
    this.routes.clear();
    this.pathIndex.clear();
    console.log('[RouteRegistry] Cleared all routes');
  }

  /**
   * Get registry statistics
   */
  getStats() {
    const rootRoutes = this.getRootRoutes();
    const totalRoutes = this.routes.size;
    const nestedRoutes = totalRoutes - rootRoutes.length;

    return {
      total: totalRoutes,
      root: rootRoutes.length,
      nested: nestedRoutes,
      byPlugin: this.getRoutesByPluginStats(),
    };
  }

  /**
   * Get route count by plugin
   */
  private getRoutesByPluginStats(): Record<string, number> {
    const stats: Record<string, number> = {};

    for (const route of this.routes.values()) {
      if (!stats[route.pluginId]) {
        stats[route.pluginId] = 0;
      }
      stats[route.pluginId]++;
    }

    return stats;
  }
}

// Global singleton key for cross-bundle sharing (Module Federation)
const REGISTRY_KEY = '__lognai_route_registry__';

/**
 * Get the global route registry instance
 * Uses globalThis to ensure the same instance is shared across Module Federation bundles
 */
export const getRouteRegistry = (): RouteRegistry => {
  const global = globalThis as unknown as { [REGISTRY_KEY]?: RouteRegistry };
  if (!global[REGISTRY_KEY]) {
    global[REGISTRY_KEY] = new RouteRegistryImpl();
  }
  return global[REGISTRY_KEY];
};

/**
 * Create a new route registry instance (for testing)
 */
export const createRouteRegistry = (): RouteRegistry => {
  return new RouteRegistryImpl();
};
